import React from 'react';
import { HeaderSettings } from './types';
import { School, Maximize2 } from 'lucide-react';

interface CustomHeaderProps {
  settings: HeaderSettings;
  isEditable?: boolean;
  selectedElement?: string | null;
  onElementSelect?: (element: 'logo' | 'schoolName' | 'address' | 'motto' | null) => void;
  onLogoMouseDown?: (e: React.MouseEvent) => void;
  onResizeMouseDown?: (e: React.MouseEvent) => void;
}

export const CustomHeader: React.FC<CustomHeaderProps> = ({ 
  settings, 
  isEditable = false,
  selectedElement = null,
  onElementSelect,
  onLogoMouseDown,
  onResizeMouseDown
}) => {
  const { layout, logo, schoolName, address, motto, spacing, separator } = settings;

  // Separator line component
  const SeparatorLine = () => {
    if (!separator?.enabled) return null;
    const horizontalPadding = separator.horizontalPadding ?? 0;
    const marginTop = separator.marginTop ?? 10;
    const marginBottom = separator.marginBottom ?? 10;
    const thickness = separator.thickness ?? 1;
    const color = separator.color ?? '#e2e8f0';
    return (
      <div 
        style={{
          marginTop: `${marginTop}px`,
          marginBottom: `${marginBottom}px`,
          position: 'relative',
          width: '100%',
          overflow: 'visible'
        }}
      >
        <div 
          style={{
            height: `${thickness}px`,
            backgroundColor: color,
            width: `calc(100% + ${horizontalPadding * 2}px)`,
            transform: `translateX(-${horizontalPadding}px)`,
          }}
        />
      </div>
    );
  };

  const getFontFamily = (family: string) => {
    switch (family) {
      case 'serif': return 'font-serif';
      case 'mono': return 'font-mono';
      default: return 'font-sans';
    }
  };

  const handleSelect = (e: React.MouseEvent, id: 'logo' | 'schoolName' | 'address' | 'motto') => {
      if (isEditable && onElementSelect) {
          e.stopPropagation();
          onElementSelect(id);
      }
  };

  const getSelectionClass = (id: string) => {
      if (!isEditable) return '';
      if (selectedElement === id) return 'outline outline-2 outline-indigo-500 outline-dashed bg-indigo-50/10 cursor-default rounded';
      return 'hover:outline hover:outline-1 hover:outline-indigo-300 hover:bg-indigo-50/5 cursor-pointer rounded transition-all duration-200';
  };

  const LogoComponent = () => {
    // In standard layout, we need the container to be full width to allow alignment
    // unless it's 'classic' where it sits side-by-side.
    const isStandard = !logo.customPositioning;
    
    const style: React.CSSProperties = !isStandard
      ? {
          position: 'absolute',
          left: `${logo.x}px`,
          top: `${logo.y}px`,
          width: `${logo.width}px`,
          cursor: isEditable ? 'move' : 'default',
          zIndex: 50,
          userSelect: 'none',
          touchAction: 'none'
        }
      : { 
          // For standard layout, this container wraps the image.
          // We control width via the image itself or this container.
          // To support alignment, this container should probably expand or we use flex on parent.
          // Let's keep it simple: This container IS the logo wrapper.
          width: layout === 'classic' ? 'auto' : '100%', 
          display: 'flex',
          justifyContent: logo.alignment === 'center' ? 'center' : (logo.alignment === 'right' ? 'flex-end' : 'flex-start'),
          padding: `${logo.padding}px`,
        };
    
    const isSelected = selectedElement === 'logo';

    return (
      <div 
        style={style}
        onMouseDown={(e) => {
            if (isEditable && logo.customPositioning) {
                handleSelect(e, 'logo');
                onLogoMouseDown?.(e);
            }
        }}
        onClick={(e) => !logo.customPositioning && handleSelect(e, 'logo')}
        className={`${isEditable && logo.customPositioning ? (isSelected ? 'border-2 border-indigo-500 border-dashed bg-indigo-50/20' : 'hover:border-2 hover:border-indigo-300 hover:border-dashed') : getSelectionClass('logo')} transition-colors relative group`}
      >
        {logo.url ? (
          <img 
            src={logo.url} 
            alt="School Logo" 
            style={{ width: isStandard ? `${logo.width}px` : '100%' }}
            className="h-auto object-contain select-none pointer-events-none" 
            draggable={false}
          />
        ) : (
          <div 
            style={{ width: isStandard ? `${logo.width}px` : '100%' }}
            className={`aspect-square bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 select-none ${!logo.customPositioning ? 'border-2 border-dashed border-slate-300' : ''}`}
          >
            <School size={logo.width * 0.5} />
          </div>
        )}

        {/* Edit Controls (Only visible in edit mode + custom positioning) */}
        {isEditable && logo.customPositioning && isSelected && (
          <>
            <div className="absolute -top-6 left-0 bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded shadow-sm whitespace-nowrap pointer-events-none z-50">
              Drag to move
            </div>
            <div 
              className="absolute -bottom-2 -right-2 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center cursor-nwse-resize shadow-md hover:scale-110 transition-transform z-50"
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent drag start
                onResizeMouseDown?.(e);
              }}
            >
               <Maximize2 size={12} className="text-white" />
            </div>
          </>
        )}
      </div>
    );
  };

  const SchoolInfoComponent = ({ alignOverride }: { alignOverride?: 'left' | 'right' | 'center' }) => {
    const nameAlignment = alignOverride || schoolName.alignment;
    const mottoAlignment = alignOverride || motto.alignment;
    const addressAlignment = alignOverride || address.alignment;
    
    const getFlexAlign = (align: string) => {
      if (align === 'center') return 'center';
      if (align === 'right') return 'flex-end';
      return 'flex-start';
    };
    
    return (
    <div className="flex flex-col w-full">
      {/* School Name */}
      <div 
        className={`mb-1 p-1 -m-1 ${getSelectionClass('schoolName')}`}
        onClick={(e) => handleSelect(e, 'schoolName')}
        style={{ 
          alignSelf: getFlexAlign(nameAlignment),
          textAlign: nameAlignment 
        }}
      >
        <h1 
            className={`${getFontFamily(schoolName.fontFamily)} leading-tight`}
            style={{ 
            fontSize: `${schoolName.fontSize}px`, 
            fontWeight: schoolName.bold ? 'bold' : 'normal',
            color: schoolName.color,
            }}
        >
            {schoolName.text}
        </h1>
      </div>
      
      {/* Motto */}
      {motto.text && (
        <div 
            className={`mb-2 p-1 -m-1 ${getSelectionClass('motto')}`}
            onClick={(e) => handleSelect(e, 'motto')}
            style={{ 
                textAlign: mottoAlignment,
                alignSelf: getFlexAlign(mottoAlignment)
            }}
        >
            <div 
            style={{ 
                color: motto.color,
                fontStyle: motto.italic ? 'italic' : 'normal',
                fontWeight: motto.bold ? 'bold' : 'normal'
            }}
            >
            {motto.text}
            </div>
        </div>
      )}
      
      {motto.showDivider && !separator?.enabled && <div className="h-px bg-slate-200 w-full my-2"></div>}

      {/* Address */}
      <div 
        className={`p-1 -m-1 ${getSelectionClass('address')}`}
        onClick={(e) => handleSelect(e, 'address')}
        style={{ 
          alignSelf: getFlexAlign(addressAlignment),
          textAlign: addressAlignment,
          width: addressAlignment === 'center' ? '100%' : 'auto'
        }}
      >
        <div 
            style={{ 
            fontSize: `${address.fontSize}px`,
            lineHeight: address.lineHeight,
            textAlign: addressAlignment,
            color: address.color,
            whiteSpace: 'pre-line'
            }}
        >
            {address.text}
        </div>
      </div>
    </div>
  );
  };

  // --- Render Logic ---

  if (logo.customPositioning) {
     const containerWidth = 794; 
     const midPoint = containerWidth / 2;
     const logoRightEdge = logo.x + logo.width;
     const logoCenter = logo.x + (logo.width / 2);

     let textPaddingLeft = 0;
     let textPaddingRight = 0;
     let minHeight = 0;

     if (logoCenter < midPoint) {
         textPaddingLeft = Math.max(0, logoRightEdge + 24);
     } else {
         textPaddingRight = Math.max(0, (containerWidth - logo.x) + 24);
     }

     minHeight = logo.y + logo.width + 20;

     const containerStyle: React.CSSProperties = {
        marginTop: `${spacing.marginTop}px`,
        marginBottom: `${spacing.marginBottom}px`,
        position: 'relative', 
        minHeight: `${Math.max(150, minHeight)}px`,
        display: 'block',
        transform: `scale(${settings.scale || 1})`,
        transformOrigin: 'top center'
      };

     const textWrapperStyle: React.CSSProperties = {
         paddingLeft: `${textPaddingLeft}px`,
         paddingRight: `${textPaddingRight}px`,
         transition: 'padding 0.2s ease-out',
         width: '100%',
     };

     return (
       <div 
         className="w-full" 
         style={containerStyle} 
         onClick={() => isEditable && onElementSelect?.(null)} // Deselect when clicking empty space
        >
          <LogoComponent />
          <div style={textWrapperStyle}>
            {layout === 'centered' ? (
                <div className="w-full flex flex-col items-center">
                    <SchoolInfoComponent alignOverride="center" />
                </div>
            ) : layout === 'two-column' ? (
                 <div className="w-full flex justify-between items-center gap-8">
                    <div className="flex-1"><SchoolInfoComponent alignOverride="left" /></div>
                 </div>
            ) : (
                <SchoolInfoComponent />
            )}
          </div>
          <SeparatorLine />
       </div>
     );
  }

  // --- Standard Layouts (Logo in flow) ---

  const standardContainerStyle: React.CSSProperties = {
    marginTop: `${spacing.marginTop}px`,
    marginBottom: `${spacing.marginBottom}px`,
    position: 'relative',
    transform: `scale(${settings.scale || 1})`,
    transformOrigin: 'top center'
  };

  const handleContainerClick = (e: React.MouseEvent) => {
      // Only deselect if clicking directly on the container, not children
      if (e.target === e.currentTarget && isEditable) {
          onElementSelect?.(null);
      }
  };

  if (layout === 'centered') {
    return (
      <div className="w-full" style={standardContainerStyle} onClick={handleContainerClick}>
        <div className="flex flex-col items-center">
          <div className="mb-4" style={{ marginBottom: '16px' }}><LogoComponent /></div>
          <div className="w-full max-w-2xl"><SchoolInfoComponent alignOverride="center" /></div>
        </div>
        <SeparatorLine />
      </div>
    );
  }

  if (layout === 'two-column') {
    return (
      <div className="w-full" style={standardContainerStyle} onClick={handleContainerClick}>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
             <div style={{ marginRight: '16px' }}>
               <LogoComponent />
             </div>
             <div className="flex flex-col items-start text-left">
                {/* Manual decomposition for two-column to allow clicking */}
                <div 
                   className={`mb-1 p-1 -m-1 ${getSelectionClass('schoolName')}`}
                   onClick={(e) => handleSelect(e, 'schoolName')}
                >
                    <h1 
                      className={`${getFontFamily(schoolName.fontFamily)} leading-tight`}
                      style={{ 
                        fontSize: `${schoolName.fontSize}px`, 
                        fontWeight: schoolName.bold ? 'bold' : 'normal',
                        color: schoolName.color,
                      }}
                    >
                      {schoolName.text}
                   </h1>
                </div>
                <div 
                  className={`p-1 -m-1 ${getSelectionClass('motto')}`}
                  onClick={(e) => handleSelect(e, 'motto')}
                >
                    <div 
                      style={{ 
                         textAlign: 'left',
                         color: motto.color,
                         fontStyle: motto.italic ? 'italic' : 'normal',
                         fontWeight: motto.bold ? 'bold' : 'normal',
                         fontSize: '12px'
                      }}
                    >
                      {motto.text}
                    </div>
                </div>
             </div>
          </div>
          <div className={`text-right flex-1 flex flex-col items-end p-1 -m-1 ${getSelectionClass('address')}`} onClick={(e) => handleSelect(e, 'address')}>
               <div 
                  style={{ 
                    fontSize: `${address.fontSize}px`,
                    lineHeight: address.lineHeight,
                    textAlign: 'right',
                    color: address.color,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {address.text}
                </div>
          </div>
        </div>
        <SeparatorLine />
      </div>
    );
  }

  // Classic (Default)
  return (
    <div className="w-full" style={standardContainerStyle} onClick={handleContainerClick}>
      <div className="flex items-center">
        <div className="shrink-0" style={{ marginRight: '24px' }}>
          <LogoComponent />
        </div>
        <div className="flex-1">
          <SchoolInfoComponent />
        </div>
      </div>
      <SeparatorLine />
    </div>
  );
};